import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Alert, Box, Typography, Paper, LinearProgress, Chip,
    Divider, TextField, IconButton, Tooltip,
} from "@mui/material";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import TrendingDownIcon  from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import ChevronLeftIcon   from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon  from "@mui/icons-material/ChevronRight";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { formatPercent, formatNumber } from "../../../../util/numberFormat";
import { ds } from "../../../../styles/designSystem";
import LoadingDots from "../../../../component/common/LoadingDots";
import { FINANCE_NAV, tabUrl } from "../../../../config/moduleNav";
import CashRunwayChart from "../../components/widget/CashRunwayChart";
import UpcomingPaymentsBoard from "../../components/widget/UpcomingPaymentsBoard";
import ReceivableOutlook from "../../components/widget/ReceivableOutlook";

// 오늘 기준 60일 뒤 (yyyy-MM-dd)
function todayPlus(days) {
    const d = new Date(); d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function today() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function pad(n) { return String(n).padStart(2, "0"); }
function toYM(year, month) { return `${year}-${pad(month)}`; }
function fromYM(ym) {
    const [y, m] = ym.split("-").map(Number);
    return { year: y, month: m };
}
function addMonths(ym, n) {
    const { year, month } = fromYM(ym);
    const d = new Date(year, month - 1 + n, 1);
    return toYM(d.getFullYear(), d.getMonth() + 1);
}
/** 배지 레이블: 같은 해면 "N월", 다른 해면 "N월('YY)" */
function badgeLabel(ym, baseYm) {
    const { year: y, month: m } = fromYM(ym);
    const { year: by } = fromYM(baseYm);
    return y !== by ? `${m}월('${String(y).slice(2)})` : `${m}월`;
}

function VsBadge({ label, diff, pct }) {
    if (diff == null && pct == null) return null;
    // 색상·아이콘은 백엔드 % 기준
    const pos = (pct ?? 0) >= 0;
    const amtSign = diff != null && diff > 0 ? "+" : "";
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.1, marginBottom: 0.5,
            bgcolor: pos ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${pos ? "#86efac" : "#fca5a5"}`,
            borderRadius: 1, px: 0.8, py: 0.3, width: "fit-content" }}>
            {pos ? <TrendingUpIcon sx={{ fontSize: 12, color: "#16a34a" }} />
                 : <TrendingDownIcon sx={{ fontSize: 12, color: "#dc2626" }} />}
            <Typography component="span" sx={{ fontSize: 11, fontWeight: 700,
                color: pos ? "#16a34a" : "#dc2626", lineHeight: 1.5 }}>
                {label}
                {diff != null && (
                    <Box component="span" sx={{ color: "#475569", fontWeight: 600, mx: 0.5 }}>
                        {amtSign}{fmtKRWFull(diff)}
                    </Box>
                )}
                {pct != null && formatPercent(pct, 1, true)}
            </Typography>
        </Box>
    );
}

const KpiCard = ({ icon, label, value, children }) => (
    <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box sx={{ color: "#2563eb", display: "flex" }}>{icon}</Box>
            <Typography sx={{ ...ds.typography.label }}>{label}</Typography>
        </Box>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, letterSpacing: "-0.3px" }}>
            {value}
        </Typography>
        {children}
    </Paper>
);

// 현금흐름 요약용 소형 stat — 라벨 + 큰 숫자 + 보조 텍스트
const MiniStat = ({ label, value, color = "#0f172a", sub }) => (
    <Box sx={{ flex: 1, minWidth: 148 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#94a3b8" }}>{label}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.25, letterSpacing: "-0.3px", whiteSpace: "nowrap" }}>
            {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{sub}</Typography>}
    </Box>
);

const statusColor = (s) => s === "CONFIRMED" ? "success" : s === "REJECTED" ? "error" : "warning";
const statusLabel = (s) => s === "CONFIRMED" ? "지출확정" : s === "REJECTED" ? "거절" : "처리대기";

export default function DashboardTab() {
    const history = useHistory();
    const nowDate  = new Date();
    const nowYM    = toYM(nowDate.getFullYear(), nowDate.getMonth() + 1);

    const [selectedYM, setSelectedYM] = useState(nowYM);
    const [data, setData]             = useState(null);
    // 현금흐름표(향후 예정 입출금·누적 잔액·빵꾸 시점) — 월 선택과 무관하게 오늘~+N일 rows 로 조회
    // undefined=로딩, null=실패/권한없음(섹션 숨김), object=성공
    const [cashflow, setCashflow]     = useState(undefined);
    const [receivables, setReceivables] = useState(null);
    // 예측 기간 — 시작~종료 직접 지정. 시작을 과거로 두면 전(완료 실적)·후(예정)를 함께 볼 수 있다.
    const [rangeFrom, setRangeFrom] = useState(todayPlus(0));    // 기본: 오늘
    const [rangeTo,   setRangeTo]   = useState(todayPlus(60));   // 기본: +60일

    // 팀 클릭 → 수입/지출 목록을 그 팀·선택한 달로 필터링해서 이동
    const goTeamTxList = (deptId) =>
        history.push(`${tabUrl(FINANCE_NAV, "view", "입/출금 목록")}&deptId=${deptId}&ym=${selectedYM}`);

    const handlePrev = () => {
        const { year, month } = fromYM(selectedYM);
        const d = new Date(year, month - 2, 1);
        setSelectedYM(toYM(d.getFullYear(), d.getMonth() + 1));
    };
    const handleNext = () => {
        const { year, month } = fromYM(selectedYM);
        const d = new Date(year, month, 1);
        if (toYM(d.getFullYear(), d.getMonth() + 1) > nowYM) return;
        setSelectedYM(toYM(d.getFullYear(), d.getMonth() + 1));
    };

    useEffect(() => {
        const { year, month } = fromYM(selectedYM);
        setData(null);
        financeApi.getDashboard(year, month).then(setData).catch(() => setData(null));
    }, [selectedYM]);

    // 현금흐름 — 오늘~+runwayDays일 rows 를 받아 잔액 곡선·지급 예정 보드에 사용.
    // 요약값(minBalance·firstNegativeDate 등)은 이 구간 전체 기준으로 함께 계산돼 내려온다.
    // 기간을 바꾸면 재조회(빵꾸 시점·기말/최저 잔액이 그 구간 기준으로 다시 계산됨).
    useEffect(() => {
        if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return;
        financeApi.getCashFlow({ from: rangeFrom, to: rangeTo, size: 2000 })
            .then(setCashflow).catch(() => setCashflow(null));
    }, [rangeFrom, rangeTo]);

    // 미수금은 기간과 무관 — 최초 1회만 조회
    useEffect(() => {
        financeApi.getReceivables(false).then(setReceivables).catch(() => setReceivables([]));
    }, []);

    const { year, month } = fromYM(selectedYM);
    const isCurrentMonth = selectedYM === nowYM;
    const monthLabel = `${year}년 ${month}월`;

    const prevYM    = addMonths(selectedYM, -1);
    const nextYM    = addMonths(selectedYM,  1);
    // 후월: 선택월이 과거이고 next ≤ 현재월
    const showNext    = !isCurrentMonth && nextYM <= nowYM;
    // 현월: 선택월이 과거이고 next < 현재월 (next ≠ current)
    const showCurrent = !isCurrentMonth && nextYM < nowYM;
    // 레이블이 현재월을 가리키면 "(현월)" 표시
    const labelOf = (ym) => ym === nowYM
        ? `${badgeLabel(ym, selectedYM)} (현월) 대비`
        : `${badgeLabel(ym, selectedYM)} 대비`;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

            {/* 월 선택기 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton size="small" onClick={handlePrev}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <TextField
                    size="small" type="month" value={selectedYM}
                    onChange={e => { if (e.target.value <= nowYM) setSelectedYM(e.target.value); }}
                    inputProps={{ max: nowYM }}
                    sx={{ width: 150 }}
                />
                <Tooltip title={isCurrentMonth ? "이번달입니다" : "다음 달로"}>
                    <span>
                        <IconButton size="small" onClick={handleNext} disabled={isCurrentMonth}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {isCurrentMonth && (
                    <Chip label="이번달" size="small" sx={{ bgcolor: "#dbeafe", color: "#2563eb", fontWeight: 700 }} />
                )}
                {!isCurrentMonth && (
                    <Chip label={monthLabel} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600 }} />
                )}
            </Box>

            {!data ? (
                <LoadingDots />
            ) : (
                <>
                    {/* KPI 카드 */}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <KpiCard icon={<TrendingUpIcon />} label={`${monthLabel} 매출`} value={fmtKRWFull(data.monthlyIncome)}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, mt: 0.6 }}>
                                <VsBadge
                                    label={labelOf(prevYM)}
                                    diff={data.incomeDiffPrevMonth}
                                    pct={data.incomeVsPrevMonth} />
                                {showNext && (
                                    <VsBadge
                                        label={labelOf(nextYM)}
                                        diff={data.incomeDiffNextMonth}
                                        pct={data.incomeVsNextMonth} />
                                )}
                                {showCurrent && (
                                    <VsBadge
                                        label={labelOf(nowYM)}
                                        diff={data.incomeDiffCurrentMonth}
                                        pct={data.incomeVsCurrentMonth} />
                                )}
                            </Box>
                        </KpiCard>

                        <KpiCard icon={<AccountBalanceWalletIcon />} label={`${monthLabel} 지출`} value={fmtKRWFull(data.monthlyExpense)}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mt: 0.5 }}>
                                {data.budgetTotal > 0
                                    ? `예산 대비 ${formatPercent((data.budgetUsed / data.budgetTotal) * 100, 0)} · 잔여 ${fmtKRWFull(data.budgetRemaining)}`
                                    : "예산 미설정"}
                            </Typography>
                        </KpiCard>

                        <KpiCard icon={<WarningAmberIcon />} label="미수금" value={fmtKRWFull(data.receivableTotal)}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#d97706", mt: 0.5 }}>
                                {formatNumber(data.receivableCount)}건{data.receivableOldestDate ? ` · 최장 ${data.receivableOldestDate}` : ""}
                            </Typography>
                        </KpiCard>

                        <KpiCard icon={<TrendingUpIcon />} label="영업이익" value={fmtKRWFull(data.operatingProfit)}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 0.5,
                                color: data.operatingProfit >= 0 ? "#16a34a" : "#dc2626" }}>
                                수익률 {formatPercent(data.profitMargin, 1)}
                            </Typography>
                        </KpiCard>
                    </Box>

                    {/* 현금흐름 요약 — 향후 예정 입출금·누적 잔액·자금 부족(빵꾸) 시점.
                        cashflow === null (권한없음/실패) 이면 섹션 자체를 숨긴다. */}
                    {cashflow !== null && (
                    <Paper sx={ds.card}>
                        <Box sx={{ px: 2.5, pt: 2, pb: 1, display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }}>
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography sx={ds.typography.sectionTitle}>현금흐름 요약</Typography>
                                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}>
                                    {cashflow?.baseBalance
                                        ? `${cashflow.baseBalance.baseDate} 잔액 기준 · ${rangeFrom} ~ ${rangeTo} 예정 입출금`
                                        : `기준 잔액 미설정 · ${rangeFrom} ~ ${rangeTo} 예정 입출금`}
                                    {" · 자세한 내역은 "}<b>자금현황 › 현금흐름표</b>
                                </Typography>
                            </Box>
                            {/* 예측 기간 — 시작~종료 직접 지정 (시작을 과거로 두면 전·후를 함께 확인) */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <TextField type="date" size="small" value={rangeFrom}
                                    onChange={e => setRangeFrom(e.target.value)}
                                    inputProps={{ max: rangeTo }} sx={{ width: 150 }} />
                                <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>~</Typography>
                                <TextField type="date" size="small" value={rangeTo}
                                    onChange={e => setRangeTo(e.target.value)}
                                    inputProps={{ min: rangeFrom }} sx={{ width: 150 }} />
                            </Box>
                        </Box>
                        <Divider />
                        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                            {cashflow === undefined ? (
                                <Typography color="text.secondary" fontSize={13}><LoadingDots/></Typography>
                            ) : <>
                                {/* 빵꾸 경고 배너 */}
                                {cashflow.firstNegativeDate ? (
                                    <Alert severity="error" icon={<WarningAmberIcon />} sx={{ borderRadius: 1, fontWeight: 600 }}>
                                        {cashflow.firstNegativeDate} 부터 잔액이 마이너스가 됩니다 / 최저 {fmtKRWFull(cashflow.minBalance)} ({cashflow.minBalanceDate}).
                                    </Alert>
                                ) : cashflow.minBalance != null ? (
                                    <Alert severity="success" sx={{ borderRadius: 1 }}>
                                        향후 자금 부족 시점이 없습니다. 최저 예상 잔액 {fmtKRWFull(cashflow.minBalance)} ({cashflow.minBalanceDate}).
                                    </Alert>
                                ) : null}

                                {/* 핵심 수치 — 현재 보유·예정 입출금·기말·최저 (숫자) */}
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                    <MiniStat label="현재 보유 현금"
                                        value={cashflow.baseBalance ? fmtKRWFull(cashflow.baseBalance.amount) : "미설정"}
                                        color="#0f766e"
                                        sub={cashflow.baseBalance ? `${cashflow.baseBalance.baseDate} 기준` : "기준 잔액을 설정하세요"} />
                                    <MiniStat label="예정 입금"
                                        value={`+${fmtKRWFull(cashflow.totalScheduledIncome)}`} color={ds.finance.income} />
                                    <MiniStat label="예정 출금"
                                        value={`−${fmtKRWFull(cashflow.totalScheduledExpense)}`} color={ds.finance.expense} />
                                    <MiniStat label="기말 예상 잔액"
                                        value={fmtKRWFull(cashflow.endingBalance)}
                                        color={cashflow.endingBalance < 0 ? ds.finance.expense : ds.finance.balance} />
                                    <MiniStat label="최저 예상 잔액"
                                        value={cashflow.minBalance != null ? fmtKRWFull(cashflow.minBalance) : "—"}
                                        color={cashflow.minBalance < 0 ? ds.finance.expense : ds.finance.balance}
                                        sub={cashflow.minBalanceDate || undefined} />
                                </Box>

                                {/* 향후 N일 자금 예측 — 6개 항목 라인(점 마커) + 잔액. 차트 안 드래그로 구간 확대. */}
                                <Box sx={{ mt: 0.5 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569", mb: 0.5 }}>
                                        {rangeFrom} ~ {rangeTo} 자금 예측
                                    </Typography>
                                    <CashRunwayChart cashflow={cashflow} from={rangeFrom} to={rangeTo} />
                                </Box>
                            </>}
                        </Box>
                    </Paper>
                    )}

                    {/* 지급 예정 스케줄 보드 — 향후 30일 지급 예정(날짜순/거래처순) */}
                    {cashflow && cashflow !== null && (
                        <Paper sx={ds.card}>
                            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                                <Typography sx={ds.typography.sectionTitle}>지급 예정 스케줄</Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ p: 2.5 }}>
                                <UpcomingPaymentsBoard rows={cashflow.rows} />
                            </Box>
                        </Paper>
                    )}

                    {/* 미수금 회수 전망 — 만기일 버킷 */}
                    {receivables && receivables.length > 0 && (
                        <Paper sx={ds.card}>
                            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                                <Typography sx={ds.typography.sectionTitle}>미수금 회수 전망</Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ p: 2.5 }}>
                                <ReceivableOutlook receivables={receivables} />
                            </Box>
                        </Paper>
                    )}

                    {/* 팀별 현황 — 수입·지출·예정 총액 + 예산 대비 소진 막대 */}
                    <Paper sx={ds.card}>
                        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                            <Typography sx={ds.typography.sectionTitle}>팀별 현황 ({monthLabel})</Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}>
                                수입·지출은 {monthLabel} 실적 · <b>예정</b>은 앞으로 나갈/들어올 예정 금액(현금흐름표 기준) · 팀 클릭 시 수입/지출 목록으로 이동
                            </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                            {(data.teamExpenses || []).length === 0 ? (
                                <Typography color="text.secondary" fontSize={13}>데이터 없음</Typography>
                            ) : (
                                data.teamExpenses.map(te => (
                                    <Box key={te.deptId}
                                        onClick={() => te.deptId != null && goTeamTxList(te.deptId)}
                                        sx={{
                                            cursor: te.deptId != null ? "pointer" : "default",
                                            mx: -1, px: 1, py: 0.5, borderRadius: 1.5,
                                            "&:hover": { bgcolor: te.deptId != null ? "rgba(37,99,235,0.05)" : "transparent" },
                                        }}>
                                        {/* 팀명 + 수입/지출/예정 3수치 */}
                                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 0.6, flexWrap: "wrap" }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, minWidth: 88 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#334155" }}>
                                                    {te.deptName}
                                                </Typography>
                                                <ChevronRightRoundedIcon sx={{ fontSize: 15, color: "#cbd5e1" }} />
                                            </Box>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                                                <Box component="span" sx={{ color: "#94a3b8", mr: 0.4 }}>수입</Box>
                                                <Box component="span" sx={{ color: "#2563eb", fontWeight: 700 }}>+{fmtKRWFull(te.incomeAmount || 0)}</Box>
                                            </Typography>
                                            <Typography sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                                                <Box component="span" sx={{ color: "#94a3b8", mr: 0.4 }}>지출</Box>
                                                <Box component="span" sx={{ color: "#dc2626", fontWeight: 700 }}>−{fmtKRWFull(te.expenseAmount || 0)}</Box>
                                            </Typography>
                                            <Typography sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                                                <Box component="span" sx={{ color: "#94a3b8", mr: 0.4 }}>예정지출</Box>
                                                <Box component="span" sx={{ color: "#7c3aed", fontWeight: 700 }}>
                                                    {(te.scheduledExpense || 0) > 0 ? `−${fmtKRWFull(te.scheduledExpense)}` : "—"}
                                                </Box>
                                            </Typography>
                                            {(te.scheduledIncome || 0) > 0 && (
                                                <Typography sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                                                    <Box component="span" sx={{ color: "#94a3b8", mr: 0.4 }}>예정수입</Box>
                                                    <Box component="span" sx={{ color: "#2563eb", fontWeight: 700 }}>
                                                        +{fmtKRWFull(te.scheduledIncome)}
                                                    </Box>
                                                </Typography>
                                            )}
                                        </Box>
                                        {/* 예산 대비 소진 막대 (예산 설정 팀만) */}
                                        {te.budgetAmount > 0 && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <LinearProgress variant="determinate" value={Math.min(te.ratio || 0, 100)} sx={{
                                                    flex: 1, height: 6, borderRadius: 4, bgcolor: "#f1f5f9",
                                                    "& .MuiLinearProgress-bar": {
                                                        borderRadius: 4,
                                                        bgcolor: te.ratio > 90 ? "#dc2626" : te.ratio > 70 ? "#d97706" : "#2563eb",
                                                    },
                                                }} />
                                                <Typography sx={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", minWidth: 132, textAlign: "right" }}>
                                                    예산 {formatPercent(te.ratio, 0)} · {fmtKRWFull(te.expenseAmount)} / {fmtKRWFull(te.budgetAmount)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Paper>

                </>
            )}
            {/*
              거래처·프로젝트·결재 상세 모달은 <FinanceLinkProvider/> 가 한 곳에서 마운트.
              이 탭에서는 셀 컴포넌트만 사용하면 자동으로 클릭 인터랙션 동작.
            */}
        </Box>
    );
}
